import React, { useState, useEffect } from 'react';
import { LuX, LuUser, LuMail, LuPhone, LuMessageSquare, LuDollarSign, LuClock, LuSave, LuShield, LuTrash2, LuHistory } from 'react-icons/lu';
import { teamService } from '../../services/teamService';
import { useAuth } from '../../hooks/useAuth';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';

const MemberModal = ({ member, isOpen, onClose, onUpdate, canEdit }) => {
    const { user: currentUser } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [loadingActivity, setLoadingActivity] = useState(false);
    const [activities, setActivities] = useState([]);
    const [formData, setFormData] = useState({
        full_name: '',
        role: '',
        email: '',
        phone: '',
        line_id: '',
        whatsapp_id: '',
        commission_rate: 0
    });

    useEffect(() => {
        if (member) {
            setFormData({
                full_name: member.full_name || '',
                role: member.role || 'Sales',
                email: member.email || '',
                phone: member.phone || '',
                line_id: member.line_id || '',
                whatsapp_id: member.whatsapp_id || '',
                commission_rate: member.commission_rate || 0
            });
            fetchActivity();
            setIsEditing(false); // Reset edit mode on open
        }
    }, [member]);

    const fetchActivity = async () => {
        setLoadingActivity(true);
        const result = await teamService.getMemberActivity(member.id);
        if (result.success) {
            setActivities(result.data);
        }
        setLoadingActivity(false);
    };

    const handleSave = async () => {
        const result = await teamService.updateMember(member.id, formData);
        if (result.success) {
            toast.success('Member updated successfully');
            onUpdate(result.data);
            setIsEditing(false);
        } else {
            toast.error('Failed to update member');
        }
    };

    const StatusBadge = ({ active }) => (
        <span className={`px-2 py-1 text-xs rounded-full ${active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
            {active ? 'Active' : 'Inactive'}
        </span>
    );

    if (!isOpen || !member) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">

                    {/* Header */}
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                {member.full_name?.charAt(0) || 'U'}
                            </div>
                            {isEditing ? 'Edit Member' : 'Member Details'}
                        </h3>
                        <div className="flex items-center gap-2">
                            {canEdit && !isEditing && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    Edit
                                </button>
                            )}
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                                <SafeIcon icon={LuX} className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* Left Column: Basic Info */}
                            <div className="space-y-4">
                                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Profile Information</h4>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs text-gray-500">Full Name</label>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={formData.full_name}
                                                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            />
                                        ) : (
                                            <div className="flex items-center text-sm font-medium text-gray-900">
                                                <SafeIcon icon={LuUser} className="mr-2 h-4 w-4 text-gray-400" />
                                                {member.full_name}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-500">Email</label>
                                        <div className="flex items-center text-sm text-gray-900 mt-1">
                                            <SafeIcon icon={LuMail} className="mr-2 h-4 w-4 text-gray-400" />
                                            {member.email}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-500">Role</label>
                                        {isEditing ? (
                                            <select
                                                value={formData.role}
                                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            >
                                                <option value="Sales">Sales</option>
                                                <option value="Manager">Manager</option>
                                                <option value="Admin">Admin</option>
                                            </select>
                                        ) : (
                                            <div className="flex items-center text-sm text-gray-900 mt-1">
                                                <SafeIcon icon={LuShield} className="mr-2 h-4 w-4 text-gray-400" />
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${member.role === 'Admin' ? 'bg-purple-100 text-purple-800' :
                                                    member.role === 'Manager' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-green-100 text-green-800'
                                                    }`}>
                                                    {member.role}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-500">Commission Rate (%)</label>
                                        {isEditing ? (
                                            <input
                                                type="number"
                                                value={formData.commission_rate}
                                                onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) })}
                                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            />
                                        ) : (
                                            <div className="flex items-center text-sm text-gray-900 mt-1">
                                                <SafeIcon icon={LuDollarSign} className="mr-2 h-4 w-4 text-gray-400" />
                                                {member.commission_rate}%
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right Column: Contact & Activity */}
                            <div className="space-y-6">
                                {/* Contact Details */}
                                <div className="space-y-3">
                                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Contact Details</h4>

                                    <div>
                                        <label className="block text-xs text-gray-500">Phone</label>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={formData.phone}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            />
                                        ) : (
                                            <div className="flex items-center text-sm text-gray-900 mt-1">
                                                <SafeIcon icon={LuPhone} className="mr-2 h-4 w-4 text-gray-400" />
                                                {member.phone || '-'}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-500">Line ID</label>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={formData.line_id}
                                                onChange={(e) => setFormData({ ...formData, line_id: e.target.value })}
                                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            />
                                        ) : (
                                            <div className="flex items-center text-sm text-gray-900 mt-1">
                                                <SafeIcon icon={LuMessageSquare} className="mr-2 h-4 w-4 text-green-500" />
                                                {member.line_id || '-'}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-500">WhatsApp</label>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={formData.whatsapp_id}
                                                onChange={(e) => setFormData({ ...formData, whatsapp_id: e.target.value })}
                                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            />
                                        ) : (
                                            <div className="flex items-center text-sm text-gray-900 mt-1">
                                                <SafeIcon icon={LuMessageSquare} className="mr-2 h-4 w-4 text-green-600" />
                                                {member.whatsapp_id || '-'}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Activity Log (View Only) */}
                                {!isEditing && (
                                    <div className="pt-4 border-t border-gray-100">
                                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                                            <SafeIcon icon={LuHistory} className="mr-2 h-4 w-4" />
                                            Recent Activity (Last 4)
                                        </h4>

                                        {loadingActivity ? (
                                            <div className="text-xs text-gray-400 italic">Loading activity...</div>
                                        ) : activities.length > 0 ? (
                                            <ul className="space-y-3">
                                                {activities.map((activity) => (
                                                    <li key={activity.id} className="text-sm">
                                                        <div className="text-gray-900">{activity.description}</div>
                                                        <div className="text-xs text-gray-500 flex items-center mt-0.5">
                                                            <SafeIcon icon={LuClock} className="mr-1 h-3 w-3" />
                                                            {new Date(activity.changed_at).toLocaleDateString()}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="text-xs text-gray-400 italic">No recent activity found.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
                        {isEditing ? (
                            <>
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    <SafeIcon icon={LuSave} className="mr-2 h-4 w-4" />
                                    Save Changes
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsEditing(false)}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                            >
                                Close
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemberModal;
